import { PropsWithChildren, useRef, useState } from 'react';
import { useClickAway, useEvent } from 'react-use';

import { Menu, MenuList, Portal } from '@frontend/components';

export type ContextMenuProps = PropsWithChildren<unknown> & {
  boundary: HTMLElement | null;
  onOpenChange?: (isOpen: boolean) => void;
};

type ContextMenuPosition = {
  x: number;
  y: number;
};

const MENU_OFFSET = 1;

/**
 * Renders a context menu when activated on a given `boundary`.
 *
 * It wraps `Menu` and `MenuList`, and expects `MenuItem`s or `MenuDivider`s as children.
 */
export const ContextMenu = ({
  boundary,
  onOpenChange,
  children,
}: ContextMenuProps) => {
  const [contextMenuPosition, setContextMenuPosition] =
    useState<null | ContextMenuPosition>(null);
  const menuRef = useRef(null);

  const onCloseContextMenu = () => {
    setContextMenuPosition(null);
    onOpenChange?.(false);
  };

  /** Determines if the event was a click at the position where the context menu was opened.
   * If so, we should open the browser's native context menu on the target. */
  const shouldOpenBrowserContextMenu = (event: MouseEvent) => {
    return (
      event.clientX + MENU_OFFSET === contextMenuPosition?.x &&
      event.clientY + MENU_OFFSET === contextMenuPosition?.y
    );
  };

  useEvent(
    'contextmenu',
    (event) => {
      const mouseEvent = event as MouseEvent;
      mouseEvent.stopPropagation();
      if (shouldOpenBrowserContextMenu(mouseEvent)) {
        return;
      }
      mouseEvent.preventDefault();

      // we need to offset the menu by a bit, so that the user can later open the browser's context menu on the target
      setContextMenuPosition({
        x: mouseEvent.clientX + MENU_OFFSET,
        y: mouseEvent.clientY + MENU_OFFSET,
      });
      onOpenChange?.(true);
    },
    boundary,
  );

  // alternative to Chakra's closeOnBlur, which doesn't trigger when right clicking outside the menu
  useClickAway(menuRef, (e: MouseEvent) => {
    if (shouldOpenBrowserContextMenu(e)) {
      return;
    }
    onCloseContextMenu();
  });

  return (
    contextMenuPosition && (
      <Menu
        onClose={onCloseContextMenu}
        strategy="fixed"
        isOpen
        closeOnBlur={false}
      >
        <Portal>
          <MenuList
            ref={menuRef}
            rootProps={{
              transform: `translate(${contextMenuPosition.x}px, ${contextMenuPosition.y}px)`,
            }}
          >
            {children}
          </MenuList>
        </Portal>
      </Menu>
    )
  );
};
